/**
 * safeStorage — quota-resilient localStorage wrapper.
 *
 * Feb 2026 — User-reported login crash: `QuotaExceededError` when writing
 * a tiny boolean preference (`showActiveNotes_<userId>`) on login. Root
 * cause was localStorage already over the 5-10 MB browser quota from
 * accumulated encrypted note drafts (`emr_draft_*`).  The autosave hook
 * does run a 24h-old purge every 10 min, but that hook isn't mounted yet
 * during the login phase — so a benign preference write blows up first
 * and blocks the entire app render.
 *
 * Behavior:
 *   1. Try `localStorage.setItem(key, value)` directly.
 *   2. If it throws `QuotaExceededError` (any cross-browser variant),
 *      call ``purgeOldestNoteDrafts`` to free space.
 *   3. Retry the original setItem once.
 *   4. If it STILL fails, swallow the error (best-effort) and emit a
 *      single ``console.warn`` so devs can investigate.  We don't want
 *      preference writes to crash render.
 *
 * Why purge note drafts specifically:
 *   - `emr_draft_*` is the biggest known consumer (encrypted note bodies,
 *     can be hundreds of KB each).
 *   - Drafts are recoverable: server has the canonical copy after the
 *     next autosave tick.
 *   - We do NOT touch other keys (filter prefs, column visibility, etc.)
 *     because those are user-set choices the system can't recreate.
 */

const NOTE_DRAFT_PREFIX = 'emr_draft_';
const MAX_DRAFTS_TO_PURGE_PER_ATTEMPT = 20;

const isQuotaError = (err) => {
  if (!err) return false;
  // Spec name, plus historical names browsers use.
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 || err.code === 1014 ||
    /quota/i.test(err.message || '')
  );
};

/**
 * Remove up to ``maxToPurge`` note drafts, oldest-first (best-effort).
 *
 * "Oldest" is approximated by lexicographic key order — the draft keys
 * contain a UUID, not a timestamp, so we can't sort by age directly
 * without decrypting.  Decrypting at quota-recovery time is expensive
 * and may itself fail (no key in scope here).  Lexicographic order is
 * a reasonable proxy because newer keys tend to be inserted after older
 * ones, and we also bias toward removing keys whose values are LARGE.
 */
export const purgeOldestNoteDrafts = (maxToPurge = MAX_DRAFTS_TO_PURGE_PER_ATTEMPT) => {
  const draftKeys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(NOTE_DRAFT_PREFIX)) {
        // Pair (key, size) so we can drop the largest ones first.
        let size = 0;
        try { size = (localStorage.getItem(k) || '').length; } catch { /* noop */ }
        draftKeys.push({ key: k, size });
      }
    }
  } catch {
    // localStorage may itself be inaccessible (private-mode Safari etc.).
    return 0;
  }
  // Largest drafts first — frees more space per delete.
  draftKeys.sort((a, b) => b.size - a.size);
  const removed = draftKeys.slice(0, maxToPurge);
  removed.forEach(({ key }) => {
    try { localStorage.removeItem(key); } catch { /* noop */ }
  });
  if (removed.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[safeStorage] Purged ${removed.length} note draft(s) ` +
      `(~${Math.round(removed.reduce((s, r) => s + r.size, 0) / 1024)}KB) ` +
      'to recover localStorage quota.'
    );
  }
  return removed.length;
};

/**
 * Quota-resilient localStorage.setItem.  Returns true if the value was
 * persisted, false if all retries failed (caller can decide whether to
 * surface that to the user).
 */
export const safeLocalStorageSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    if (!isQuotaError(err)) {
      // Non-quota error (e.g. SecurityError in private mode) — swallow
      // because preference writes must never crash render.
      // eslint-disable-next-line no-console
      console.warn(`[safeStorage] setItem('${key}') failed:`, err);
      return false;
    }
    // Quota: purge + retry once.
    purgeOldestNoteDrafts();
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (err2) {
      // eslint-disable-next-line no-console
      console.warn(
        `[safeStorage] setItem('${key}') still over quota after purge — ` +
        'value not persisted.',
        err2,
      );
      return false;
    }
  }
};

/**
 * Mirror of ``safeLocalStorageSetItem`` for sessionStorage (smaller
 * quota, same failure mode).  Currently a thin wrapper that swallows
 * any error since we don't have a recoverable purge strategy for
 * sessionStorage (typically only auth/short-lived flags).
 */
export const safeSessionStorageSetItem = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[safeStorage] sessionStorage.setItem('${key}') failed:`, err);
    return false;
  }
};
